import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { themeOptions, themes } from "@/lib/themes";
import { Plus, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const field = (label, value, onChange, props = {}) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Input value={value || ""} onChange={onChange} {...props} />
  </div>
);

const richField = (label, value, onChange, key, testId, minHeight = "110px") => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <RichTextEditor
      key={key}
      value={value || ""}
      onChange={onChange}
      minHeight={minHeight}
      data-testid={testId}
    />
  </div>
);

export default function EventSettings() {
  const { eventId, refreshEvents } = useOutletContext();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/events/${eventId}`).then((r) => setForm(r.data));
  }, [eventId]);

  if (!form)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" /></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setHtml = (k) => (html) => setForm({ ...form, [k]: html });

  const setFaq = (idx, key, val) => {
    const faqs = [...(form.faqs || [])];
    faqs[idx] = { ...faqs[idx], [key]: val };
    setForm({ ...form, faqs });
  };
  const addFaq = () => setForm({ ...form, faqs: [...(form.faqs || []), { question: "", answer: "" }] });
  const removeFaq = (idx) => setForm({ ...form, faqs: form.faqs.filter((_, i) => i !== idx) });

  const setContact = (idx, key, val) => {
    const contacts = [...(form.contacts || [])];
    contacts[idx] = { ...contacts[idx], [key]: val };
    setForm({ ...form, contacts });
  };
  const addContact = () => setForm({ ...form, contacts: [...(form.contacts || []), { name: "", role: "", phone: "", email: "" }] });
  const removeContact = (idx) => setForm({ ...form, contacts: form.contacts.filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      const { id, created_at, ...payload } = form;
      await api.put(`/events/${eventId}`, payload);
      await refreshEvents();
      toast.success("Event saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const section = (title, children) => (
    <div className="rounded-md border border-[#E5E5E5] bg-white p-6">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[#525252]">{title}</h2>
      {children}
    </div>
  );

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Event Settings</h1>
        <Button onClick={save} disabled={saving} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="save-event-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
        </Button>
      </div>

      {section("Basics", (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">{field("Event Name", form.name, set("name"), { "data-testid": "event-name-input" })}</div>
          <div className="col-span-2">{field("Subtitle", form.subtitle, set("subtitle"))}</div>
          {field("Partner One", form.partner_one, set("partner_one"))}
          {field("Partner Two", form.partner_two, set("partner_two"))}
          {field("Start Date", form.start_date, set("start_date"), { type: "date" })}
          {field("End Date", form.end_date, set("end_date"), { type: "date" })}
          {field("RSVP Deadline", form.rsvp_deadline, set("rsvp_deadline"), { type: "date" })}
          <div className="col-span-2 space-y-2">
            <Label>Invitation Theme</Label>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {themeOptions.map((o) => {
                const p = themes[o.value];
                const active = (form.theme || "emerald") === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setForm({ ...form, theme: o.value })}
                    data-testid={`theme-option-${o.value}`}
                    className={`rounded-lg border p-3 text-left transition-all hover:-translate-y-0.5 ${active ? "border-[#1A362D] ring-2 ring-[#1A362D]" : "border-[#E5E5E5]"}`}
                  >
                    <div className="mb-2 flex gap-1">
                      <span className="h-6 w-6 rounded-full" style={{ background: p.band }} />
                      <span className="h-6 w-6 rounded-full" style={{ background: p.accent }} />
                      <span className="h-6 w-6 rounded-full border border-[#E5E5E5]" style={{ background: p.secondary }} />
                    </div>
                    <div className="text-xs font-medium">{o.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Accent Colour (override)</Label>
              <Input type="color" value={form.theme_primary || "#1A362D"} onChange={set("theme_primary")} className="h-10 p-1" />
            </div>
          </div>
        </div>
      ))}

      {section("Venue & Location", (
        <div className="grid grid-cols-1 gap-4">
          {field("Venue", form.venue, set("venue"))}
          {field("Venue Address", form.venue_address, set("venue_address"))}
          {field("Google Maps Link", form.google_maps_link, set("google_maps_link"))}
        </div>
      ))}

      {section("Imagery", (
        <div className="grid grid-cols-1 gap-4">
          {field("Hero Image URL", form.hero_image, set("hero_image"))}
          {field("Couple Image URL", form.couple_image, set("couple_image"))}
        </div>
      ))}

      {section("Story & Content", (
        <div className="space-y-4">
          {richField("Welcome Message", form.welcome_message, setHtml("welcome_message"), `welcome-${form.id}`, "welcome-message-editor")}
          {richField("Couple / Event Story", form.couple_story, setHtml("couple_story"), `story-${form.id}`, "couple-story-editor", "140px")}
          {richField("Accommodation Details", form.accommodation, setHtml("accommodation"), `accommodation-${form.id}`, "accommodation-editor")}
          {richField("Dress Code", form.dress_code, setHtml("dress_code"), `dress-${form.id}`, "dress-code-editor", "90px")}
        </div>
      ))}

      {section("After the event", (
        <div className="grid grid-cols-1 gap-4">
          <p className="text-sm text-[#525252]">
            Save a pictures app or website link here. It appears on every personal invitation as soon as you save, and updates the next time a guest opens their link.
          </p>
          {field("Photos or app link", form.photos_url, set("photos_url"), { placeholder: "https://…" })}
          {field("Link label", form.photos_label, set("photos_label"), { placeholder: "Wedding photographs" })}
          {field("Note", form.photos_note, set("photos_note"), { placeholder: "A short line shown above the link" })}
        </div>
      ))}

      {section("Explore catalog", (
        <p className="text-sm text-[#525252]">
          Manage sightseeing, tours, and activities under{" "}
          <Link to="/admin/explore" className="font-medium text-[#1A362D] underline underline-offset-2">
            Admin → Explore
          </Link>
          . Hotel contacts are under Admin → Rooms.
        </p>
      ))}

      {section("FAQs", (
        <div className="space-y-3">
          {(form.faqs || []).map((f, idx) => (
            <div key={idx} className="rounded border border-[#E5E5E5] p-3">
              <div className="mb-2 flex items-center gap-2">
                <Input value={f.question} onChange={(e) => setFaq(idx, "question", e.target.value)} placeholder="Question" />
                <Button size="icon" variant="ghost" onClick={() => removeFaq(idx)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
              <RichTextEditor
                key={`faq-${form.id}-${idx}`}
                value={f.answer || ""}
                onChange={(html) => setFaq(idx, "answer", html)}
                placeholder="Answer"
                minHeight="90px"
                data-testid={`faq-answer-${idx}`}
              />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addFaq}><Plus className="mr-2 h-4 w-4" /> Add FAQ</Button>
        </div>
      ))}

      {section("Contacts", (
        <div className="space-y-3">
          {(form.contacts || []).map((c, idx) => (
            <div key={idx} className="grid grid-cols-4 items-end gap-2 rounded border border-[#E5E5E5] p-3">
              <div><Label className="text-xs">Name</Label><Input value={c.name} onChange={(e) => setContact(idx, "name", e.target.value)} /></div>
              <div><Label className="text-xs">Role</Label><Input value={c.role} onChange={(e) => setContact(idx, "role", e.target.value)} /></div>
              <div><Label className="text-xs">Phone</Label><Input value={c.phone} onChange={(e) => setContact(idx, "phone", e.target.value)} /></div>
              <div className="flex items-end gap-1">
                <div className="flex-1"><Label className="text-xs">Email</Label><Input value={c.email} onChange={(e) => setContact(idx, "email", e.target.value)} /></div>
                <Button size="icon" variant="ghost" onClick={() => removeContact(idx)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addContact}><Plus className="mr-2 h-4 w-4" /> Add Contact</Button>
          <div className="mt-4">{field("Emergency Contact", form.emergency_contact, set("emergency_contact"))}</div>
        </div>
      ))}
    </div>
  );
}
